/**
 * useResponsiveViewport Hook
 *
 * Automatically detects window size and returns the appropriate viewport/breakpoint
 * for responsive rendering. Updates on window resize.
 */

import { useState, useEffect, useMemo } from 'react';

export type ResponsiveBreakpoint = 'mobile' | 'tablet' | 'desktop';

export interface ResponsiveViewportConfig {
  mobile: number;    // max width for mobile
  tablet: number;    // max width for tablet
  // desktop is anything above tablet
}

const DEFAULT_BREAKPOINTS: ResponsiveViewportConfig = {
  mobile: 767,    // 0-767px = mobile
  tablet: 1024,   // 768-1024px = tablet
  // 1025px+ = desktop
};

/**
 * Hook to get the current responsive breakpoint based on window width
 */
export const useResponsiveViewport = (
  config: ResponsiveViewportConfig = DEFAULT_BREAKPOINTS
): ResponsiveBreakpoint => {
  // Initialize with actual window width (or default to desktop for SSR)
  const [breakpoint, setBreakpoint] = useState<ResponsiveBreakpoint>(() => {
    if (typeof window === 'undefined') return 'desktop';

    const width = window.innerWidth;
    const initialBreakpoint = width <= config.mobile ? 'mobile' : width <= config.tablet ? 'tablet' : 'desktop';
    return initialBreakpoint;
  });

  useEffect(() => {
    // Function to calculate breakpoint from window width
    const calculateBreakpoint = (): ResponsiveBreakpoint => {
      const width = window.innerWidth;
      const bp = width <= config.mobile ? 'mobile' : width <= config.tablet ? 'tablet' : 'desktop';
      return bp;
    };

    // Handler for window resize
    const handleResize = () => {
      const newBreakpoint = calculateBreakpoint();
      setBreakpoint(prevBreakpoint => {
        // Only update if breakpoint actually changed
        if (newBreakpoint !== prevBreakpoint) {
          return newBreakpoint;
        }
        return prevBreakpoint;
      });
    };

    // Debounce resize handler for performance
    let resizeTimeout: NodeJS.Timeout;
    const debouncedResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(handleResize, 150);
    };

    // Add resize listener
    window.addEventListener('resize', debouncedResize);

    // Initial check in case window was resized before mount
    handleResize();

    // Cleanup
    return () => {
      window.removeEventListener('resize', debouncedResize);
      clearTimeout(resizeTimeout);
    };
  }, [config.mobile, config.tablet]);

  return breakpoint;
};

/**
 * Hook to get detailed responsive information
 */
export const useResponsiveInfo = (
  config: ResponsiveViewportConfig = DEFAULT_BREAKPOINTS
) => {
  const breakpoint = useResponsiveViewport(config);
  const [windowSize, setWindowSize] = useState(() => {
    if (typeof window === 'undefined') {
      return { width: 1920, height: 1080 };
    }
    return { width: window.innerWidth, height: window.innerHeight };
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };

    let resizeTimeout: NodeJS.Timeout;
    const debouncedResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(handleResize, 150);
    };

    window.addEventListener('resize', debouncedResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', debouncedResize);
      clearTimeout(resizeTimeout);
    };
  }, []);

  const info = useMemo(() => ({
    breakpoint,
    windowWidth: windowSize.width,
    windowHeight: windowSize.height,
    isMobile: breakpoint === 'mobile',
    isTablet: breakpoint === 'tablet',
    isDesktop: breakpoint === 'desktop',
    orientation: windowSize.width > windowSize.height ? 'landscape' : 'portrait',
  }), [breakpoint, windowSize]);

  return info;
};

/**
 * Get breakpoint-specific value
 * Useful for conditional rendering or property selection
 */
export const useBreakpointValue = <T,>(values: {
  mobile: T;
  tablet: T;
  desktop: T;
}): T => {
  const breakpoint = useResponsiveViewport();
  return values[breakpoint];
};
